/**
 * [AM-NAMES 2026-09-04, user] The Account Manager roster behind the inventory
 * "AM Names" column, plus the helpers that parse / canonicalise the stored value.
 *
 * ⚠ MIRRORS server/src/services/amRoster.ts — keep the two IDENTICAL. The client and
 * server are separate packages with no shared module (the same split identityRoster
 * lives with), so these two copies are the contract. The server re-validates every
 * write against its own copy, so a drift here can only ever cost a rejected save,
 * never a bad value in the database.
 *
 * STORAGE: several names live in ONE string column (inventory.am_names), joined by
 * ', ' — the same shape the Second Voice field uses for multi-rep credit (see
 * SECOND-VOICE-MULTI in DealCreationModal). A list of one is just that name.
 *
 * ⚠ "Gaurav Kumar" is a PREFIX of "Gaurav Kumar Gaur" and they are two DIFFERENT
 * people. Never match an AM with a bare substring test — it matches both. Every
 * comparison here goes through the exact comma-delimited token.
 */

/** The eight Account Managers, in the order they appear in every dropdown. */
export const AM_NAMES: readonly string[] = [
  'Gaurav Kumar Gaur',
  'Karishma Gaur',
  'Gaurav Kumar',
  'Chandan Gupta',
  'Ajay Kumar',
  'Rajnandani Kushwaha',
  'Vijay Kumar Tiwari',
  'Manoj Yadav',
];

/** Separator for the stored value. Parsing accepts any spacing; writing emits this. */
const AM_SEP = ', ';

/** Roster position, so a stored value is always in roster order — never click order.
 *  A stable order means reopening the dropdown and re-ticking the same people
 *  produces a byte-identical value, so the save is correctly skipped as a no-op. */
const AM_ORDER = new Map(AM_NAMES.map((n, i) => [n, i]));

/** trim+lowercase → canonical spelling, so 'ajay kumar' resolves to 'Ajay Kumar'. */
const AM_BY_NORM = new Map(AM_NAMES.map((n) => [n.trim().toLowerCase(), n]));

/** The roster spelling of `name`, or null when it isn't an AM. Exact token match. */
export function canonicalAmName(name: unknown): string | null {
  return AM_BY_NORM.get(String(name ?? '').trim().toLowerCase()) ?? null;
}

/** Stored value → the AM names it holds: de-duplicated, canonically spelled and in
 *  roster order. Anything not on the roster is dropped. */
export function parseAmNames(stored: unknown): string[] {
  const out = new Set<string>();
  for (const part of String(stored ?? '').split(',')) {
    const canon = canonicalAmName(part);
    if (canon) out.add(canon);
  }
  return [...out].sort((a, b) => (AM_ORDER.get(a) ?? 0) - (AM_ORDER.get(b) ?? 0));
}

/** Names → the canonical stored string, or '' when the list is empty. The cell
 *  editor sends '' for "no AM"; the server turns that into a NULL column. */
export function joinAmNames(names: string[]): string {
  return parseAmNames(names.join(AM_SEP)).join(AM_SEP);
}

/** Re-join a stored value through the parser so ANY spacing the DB hands back
 *  ("A,B", "A ,B") compares equal to our canonical "A, B" — otherwise opening and
 *  closing the dropdown without changing anything would look like an edit. */
export function normalizeAmNames(stored: unknown): string {
  return parseAmNames(stored).join(AM_SEP);
}
